<template>
  <div class="space-y-6">
    <Message severity="info" :closable="false" size="small" class="shadow-sm">
      <div class="text-xs dark:text-[var(--p-text-color)]">
        <strong>{{ t('whatsapp.select_routing_destination.important') }}:</strong>
        {{ t('whatsapp.select_routing_destination.routing_notice') }}
      </div>
    </Message>

    <div class="flex items-start gap-4">
      <!-- <div
        class="flex items-center justify-center w-8 h-8 rounded-full bg-[var(--p-primary-color)] text-white text-sm font-semibold flex-shrink-0"
      >
        3
      </div> -->
      <div class="flex-1 space-y-4">
        <div>
          <label class="block text-sm font-medium mb-2 dark:text-[var(--p-text-color)]">
            {{ t('whatsapp.select_routing_destination.where_to_route') }}
          </label>
          <AppSelect
            v-model="selectedDestinationType"
            :options="destinationTypes"
            option-label="label"
            option-value="value"
            :placeholder="t('whatsapp.select_routing_destination.destination_type_placeholder')"
            class="w-full"
            size="small"
          >
            <template #option="slotProps">
              <div class="flex items-center gap-3 py-1">
                <Radio v-if="slotProps.option.icon === 'radio'" :size="16" />
                <User v-if="slotProps.option.icon === 'user'" :size="16" />
                <BotMessageSquare v-if="slotProps.option.icon === 'bot-message'" :size="16" />
                <div class="flex flex-col gap-1">
                  <span class="font-medium dark:text-[var(--p-text-color)]">{{
                    slotProps.option.label
                  }}</span>
                  <span
                    class="text-xs text-[var(--p-surface-500)] dark:text-[var(--p-surface-400)]"
                  >
                    {{ slotProps.option.description }}
                  </span>
                </div>
              </div>
            </template>
          </AppSelect>
        </div>

        <div v-if="selectedDestinationType === 'campaign'" class="space-y-2">
          <label class="block text-sm font-medium dark:text-[var(--p-text-color)]">
            {{ t('whatsapp.select_routing_destination.select_campaign') }}
          </label>
          <AppSelect
            v-model="selectedCampaignId"
            :options="campaigns"
            option-label="name"
            option-value="id"
            :placeholder="t('whatsapp.select_routing_destination.campaign_placeholder')"
            class="w-full"
            size="small"
            :loading="isLoadingCampaigns"
          >
            <template #option="slotProps">
              <div class="flex flex-col gap-1 py-1">
                <span class="font-medium dark:text-[var(--p-text-color)]">{{
                  slotProps.option.name
                }}</span>
                <span class="text-xs text-[var(--p-surface-500)] dark:text-[var(--p-surface-400)]">
                  {{ getCampaignTypeLabel(slotProps.option.type) }}
                </span>
              </div>
            </template>
          </AppSelect>
          <small class="text-[var(--p-surface-500)] dark:text-[var(--p-surface-400)] block">
            {{ t('whatsapp.select_routing_destination.campaign_queue_hint') }}
          </small>
        </div>

        <div v-if="selectedDestinationType === 'user'" class="space-y-2">
          <label class="block text-sm font-medium dark:text-[var(--p-text-color)]">
            {{ t('whatsapp.select_routing_destination.select_agent_campaign') }}
          </label>
          <AppSelect
            v-model="selectedUserCampaignId"
            :options="campaigns"
            option-label="name"
            option-value="id"
            :placeholder="t('whatsapp.select_routing_destination.campaign_placeholder')"
            class="w-full"
            size="small"
            :loading="isLoadingCampaigns"
          >
            <template #option="slotProps">
              <div class="flex flex-col gap-1 py-1">
                <span class="font-medium dark:text-[var(--p-text-color)]">{{
                  slotProps.option.name
                }}</span>
                <span class="text-xs text-[var(--p-surface-500)] dark:text-[var(--p-surface-400)]">
                  {{ getCampaignTypeLabel(slotProps.option.type) }}
                </span>
              </div>
            </template>
          </AppSelect>
          <!-- <small class="text-[var(--p-surface-500)] dark:text-[var(--p-surface-400)] block">
            Se mostrarán los agentes disponibles de la campaña seleccionada
          </small> -->

          <label class="block text-sm font-medium dark:text-[var(--p-text-color)] mt-3">
            {{ t('whatsapp.select_routing_destination.select_agent') }}
          </label>
          <AppSelect
            v-model="selectedUserId"
            :options="users"
            option-label="name"
            option-value="id"
            :placeholder="
              selectedUserCampaign
                ? t('whatsapp.select_routing_destination.agent_placeholder')
                : t('whatsapp.select_routing_destination.select_campaign_first')
            "
            class="w-full"
            size="small"
            :loading="isLoadingUsers"
            :disabled="!selectedUserCampaign"
          >
            <template #value="slotProps">
              <div v-if="selectedUser" class="flex items-center gap-3">
                <AppAvatar
                  :image="getUserAvatar(selectedUser)"
                  icon="pi pi-user"
                  shape="circle"
                  class="pointer-events-none [&_img]:h-full [&_img]:w-full [&_img]:object-cover [&_img]:object-center"
                />
                <span class="truncate dark:text-[var(--p-text-color)]">
                  {{ selectedUser.name }}
                </span>
              </div>
              <span v-else>
                {{ slotProps.placeholder }}
              </span>
            </template>
            <template #option="slotProps">
              <div class="flex items-center gap-3 py-1">
                <AppAvatar
                  :image="getUserAvatar(slotProps.option)"
                  icon="pi pi-user"
                  shape="circle"
                  class="[&_img]:h-full [&_img]:w-full [&_img]:object-cover [&_img]:object-center"
                />
                <div class="flex flex-col gap-1">
                  <span class="font-medium dark:text-[var(--p-text-color)]">{{
                    slotProps.option.name
                  }}</span>
                  <!-- <span
                    class="text-xs text-[var(--p-surface-500)] dark:text-[var(--p-surface-400)]"
                  >
                    {{ slotProps.option.email || slotProps.option.id }}
                  </span> -->
                </div>
              </div>
            </template>
          </AppSelect>
          <small class="text-[var(--p-surface-500)] dark:text-[var(--p-surface-400)] block">
            {{ t('whatsapp.select_routing_destination.agent_assignment_hint') }}
          </small>
        </div>

        <div v-if="selectedDestinationType === 'bot'" class="space-y-2">
          <label class="block text-sm font-semibold dark:text-[var(--p-text-color)]">
            {{ t('whatsapp.select_routing_destination.select_bot') }}
          </label>
          <AppSelect
            v-model="selectedBotId"
            :options="bots"
            option-label="name"
            option-value="id"
            :placeholder="t('whatsapp.select_routing_destination.bot_placeholder')"
            class="w-full"
            size="small"
            :loading="isLoadingBots"
          >
            <template #option="slotProps">
              <div class="flex items-center gap-3 py-1">
                <div
                  class="flex items-center justify-center w-8 h-8 rounded-full bg-[var(--p-surface-100)] dark:bg-[var(--p-surface-800)] text-[var(--p-surface-600)] dark:text-[var(--p-surface-300)]"
                >
                  <BotMessageSquare :size="14" />
                </div>
                <div class="flex flex-col gap-1">
                  <span class="font-medium dark:text-[var(--p-text-color)]">{{
                    slotProps.option.name
                  }}</span>
                  <span
                    class="text-xs text-[var(--p-surface-500)] dark:text-[var(--p-surface-400)]"
                  >
                    ID: {{ slotProps.option.id }}
                  </span>
                </div>
              </div>
            </template>
          </AppSelect>
          <small class="text-[var(--p-surface-500)] dark:text-[var(--p-surface-400)] block">
            {{ t('whatsapp.select_routing_destination.bot_assignment_hint') }}
          </small>
        </div>

        <label
          v-if="!selectedDestinationType"
          severity="warn"
          :closable="false"
          size="small"
          class="text-xs"
        >
          {{ t('whatsapp.select_routing_destination.destination_required') }}
        </label>

        <label
          v-else-if="!isDestinationComplete"
          severity="warn"
          :closable="false"
          size="small"
          class="text-xs"
        >
          {{
            t('whatsapp.select_routing_destination.incomplete_destination', {
              destination: destinationLabel,
            })
          }}
        </label>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import Message from 'primevue/message'
import { Radio, User, BotMessageSquare } from 'lucide-vue-next'
import type { RoutingDestination } from '@/components/whatsapp/types/interfaces'
import type { IOptionsCampaign } from '@/services/campaign/interfaces/options-campaign.interface'
import type { BotOption } from '@/services/bot/interfaces'
import { TypeCampaign } from '@/services/campaign/enums/type-campaign.enum'
import { useI18n } from 'vue-i18n'
import AppAvatar from '@/components/atoms/avatar/AppAvatar.vue'
import AppSelect from '@/components/atoms/selects/AppSelect.vue'

interface UserOption {
  id: string
  name: string
  email?: string
  avatar?: string | null
}

interface Props {
  campaigns: IOptionsCampaign[]
  users: UserOption[]
  bots: BotOption[]
  isLoadingCampaigns?: boolean
  isLoadingUsers?: boolean
  isLoadingBots?: boolean
  defaultCampaignId?: string | null
  defaultUserId?: string | null
}

const props = withDefaults(defineProps<Props>(), {
  isLoadingCampaigns: false,
  isLoadingUsers: false,
  isLoadingBots: false,
  defaultCampaignId: null,
  defaultUserId: null,
})
const { t } = useI18n()

const emit = defineEmits<{
  'update:destination': [destination: RoutingDestination | null]
  'load-campaigns': []
  'load-users': [campaignId: string]
  'load-bots': []
}>()

const getUserAvatar = (user: UserOption) => user.avatar?.trim() || ''

const destinationTypes = computed(() => [
  {
    value: 'campaign',
    label: t('whatsapp.select_routing_destination.campaign'),
    description: t('whatsapp.select_routing_destination.campaign_description'),
    icon: 'radio',
  },
  {
    value: 'user',
    label: t('whatsapp.select_routing_destination.agent'),
    description: t('whatsapp.select_routing_destination.agent_description'),
    icon: 'user',
  },
  {
    value: 'bot',
    label: 'Bot',
    description: t('whatsapp.select_routing_destination.bot_description'),
    icon: 'bot-message',
  },
])

const selectedDestinationType = ref<'campaign' | 'user' | 'bot' | null>(
  props.defaultUserId ? 'user' : 'campaign',
)
const selectedCampaignId = ref<string | null>(null)
const selectedUserCampaignId = ref<string | null>(null)
const selectedUserId = ref<string | null>(null)
const selectedBotId = ref<string | null>(null)
const selectedCampaign = computed(
  () => props.campaigns.find((campaign) => campaign.id === selectedCampaignId.value) ?? null,
)
const selectedUserCampaign = computed(
  () => props.campaigns.find((campaign) => campaign.id === selectedUserCampaignId.value) ?? null,
)
const selectedUser = computed(
  () => props.users.find((user) => user.id === selectedUserId.value) ?? null,
)
const selectedBot = computed(
  () => props.bots.find((bot) => bot.id === selectedBotId.value) ?? null,
)

const getCampaignTypeLabel = (type: TypeCampaign): string => {
  const labels: Record<TypeCampaign, string> = {
    [TypeCampaign.BLASTING]: 'Blasting',
    [TypeCampaign.INCOMING]: t('whatsapp.select_routing_destination.incoming'),
    [TypeCampaign.OUTGOING]: t('whatsapp.select_routing_destination.outgoing'),
    [TypeCampaign.WHATSAPP]: 'WhatsApp',
    [TypeCampaign.PREVIEW]: 'Preview',
  }
  return labels[type] || type
}

const destinationLabel = computed(() => {
  switch (selectedDestinationType.value) {
    case 'campaign':
      return t('whatsapp.select_routing_destination.a_campaign')
    case 'user':
      return t('whatsapp.select_routing_destination.a_campaign_and_agent')
    case 'bot':
      return t('whatsapp.select_routing_destination.a_bot')
    default:
      return ''
  }
})

const isDestinationComplete = computed(() => {
  switch (selectedDestinationType.value) {
    case 'campaign':
      return selectedCampaign.value !== null
    case 'user':
      return selectedUserCampaign.value !== null && selectedUser.value !== null
    case 'bot':
      return selectedBot.value !== null
    default:
      return false
  }
})

const buildDestination = (): RoutingDestination | null => {
  if (!isDestinationComplete.value) return null

  switch (selectedDestinationType.value) {
    case 'campaign':
      return {
        type: 'campaign',
        campaign_id: selectedCampaign.value!.id,
        campaign_name: selectedCampaign.value!.name,
      }
    case 'user':
      return {
        type: 'user',
        user_id: selectedUser.value!.id,
        user_name: selectedUser.value!.name,
        campaign_id: selectedUserCampaign.value!.id,
        campaign_name: selectedUserCampaign.value!.name,
      }
    case 'bot':
      return {
        type: 'bot',
        bot_id: selectedBot.value!.id,
        bot_name: selectedBot.value!.name,
      }
    default:
      return null
  }
}

watch(selectedDestinationType, (newType) => {
  selectedCampaignId.value = null
  selectedUserCampaignId.value = null
  selectedUserId.value = null
  selectedBotId.value = null

  if (newType === 'campaign') {
    emit('load-campaigns')
  } else if (newType === 'user') {
    emit('load-campaigns')
  } else if (newType === 'bot') {
    emit('load-bots')
  }

  emit('update:destination', null)
})

watch(
  () => [props.campaigns, props.defaultCampaignId] as const,
  ([campaigns, defaultCampaignId]) => {
    if (campaigns.length === 0) return

    if (selectedDestinationType.value === 'campaign') {
      const currentCampaignStillExists = selectedCampaign.value
        ? campaigns.some((campaign) => campaign.id === selectedCampaign.value?.id)
        : false

      if (currentCampaignStillExists) return

      selectedCampaignId.value =
        campaigns.find((campaign) => campaign.id === defaultCampaignId)?.id ?? campaigns[0]?.id ?? null
    } else if (selectedDestinationType.value === 'user' && !selectedUserCampaign.value) {
      selectedUserCampaignId.value = campaigns.find((campaign) => campaign.id === defaultCampaignId)?.id ?? null
    }
  },
  { immediate: true },
)

watch(
  () => props.users,
  (users) => {
    if (
      !props.defaultUserId ||
      selectedDestinationType.value !== 'user' ||
      selectedUser.value !== null ||
      users.length === 0
    )
      return

    selectedUserId.value = users.find((user) => String(user.id) === String(props.defaultUserId))?.id ?? null
  },
)

watch(selectedUserCampaignId, (campaignId) => {
  selectedUserId.value = null

  if (selectedDestinationType.value === 'user' && campaignId) {
    emit('load-users', campaignId)
  }

  emit('update:destination', buildDestination())
})

watch([selectedCampaignId, selectedUserId, selectedBotId], () => {
  emit('update:destination', buildDestination())
})

onMounted(() => {
  emit('load-campaigns')
})
</script>
